<script setup lang="ts">
import { IS_BAYER_APP } from '@/config/customers'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

interface Props {
  modelValue: string | number
  options: Array<{ name: string; value: string | number; helpText?: string }>
  placeholder?: string
  required?: boolean
  bg?: string
  selectExtraClasses?: string
  enableAllOption?: boolean
  enableNoneOption?: boolean
  allOptionLabel?: string
  defaultArrowIcon?: boolean
  optionTextClasses?: string
  width?: number
  textCase?: 'uppercase' | 'lowercase' | 'capitalize'
  initialMinWidth?: boolean
  boxRadius?: '0' | '1' | '2' | '3'
  disabled?: boolean
  minWidth?: string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  required: false,
  bg: '',
  selectExtraClasses: '',
  enableAllOption: false,
  defaultArrowIcon: true,
  allOptionLabel: '',
  optionTextClasses: 'text-info',
  initialMinWidth: false,
  boxRadius: '2',
  disabled: false,
})
const emits = defineEmits<{
  (event: 'update:modelValue', modelValue: string | number): void
}>()

const style = computed(() => {
  if (props.initialMinWidth) {
    return { minWidth: 'initial' }
  }

  if (props.minWidth) {
    return { minWidth: props.minWidth }
  }

  return props.width ? { width: `${props.width}px` } : {}
})
</script>

<template>
  <div class="d-flex field-select align-self-center" style="height: fit-content">
    <select
      :value="modelValue"
      class="form-select letter-spacing-05 text-truncate cursor-pointer"
      :class="[
        modelValue ? props.optionTextClasses : 'text-muted',
        bg ? bg : 'bg-white',
        selectExtraClasses ? selectExtraClasses : '',
        { 'w-fit-content': props.initialMinWidth },
        { 'field-select-simple-select': !props.initialMinWidth },
        `rounded-${props.boxRadius}`,
        defaultArrowIcon ? '' : 'px-2',
        { 'text-center': !defaultArrowIcon && IS_BAYER_APP },
      ]"
      :style="style"
      :required="required"
      :disabled="props.disabled"
      @input="emits('update:modelValue', ($event.target as HTMLInputElement).value)"
    >
      <option v-if="props.placeholder && !props.modelValue" value="" disabled selected>{{ props.placeholder }}</option>
      <option v-if="props.modelValue && enableAllOption" value="">{{ props.allOptionLabel || t('All') }}</option>
      <option v-if="props.modelValue && enableNoneOption" value="">{{ t('None') }}</option>
      <option
        v-for="option in options"
        :key="option.value"
        :value="option.value"
        :title="option.helpText"
        :class="'text-' + props.textCase"
      >
        {{ option.name }}
      </option>
    </select>

    <label v-if="!props.initialMinWidth" class="d-flex align-self-center field-select-arrow">
      <i-entypo-select-arrows v-if="defaultArrowIcon" class="mx-3 text-info" style="height: 0.85rem" />
      <slot name="icon"></slot>
    </label>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/variables';

select:disabled {
  pointer-events: none;
  color: rgba($info, 0.3) !important;
}
</style>
